import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { getSeatingCategories, getSeatingAreas } from "../api/dashboardApi";
import { useNavigate, useLocation } from "react-router-dom";


export default function Dashboard() {
  const { user } = useAuth();

  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [seatingAreas, setSeatingAreas] = useState([]);
  const [seatingAreasLoading, setSeatingAreasLoading] = useState(false);
  const [seatingAreasError, setSeatingAreasError] = useState(false);
  const [touchSeat, setTouchSeat] = useState(null);
  const navigate = useNavigate();

  // Extract variables to stable primitives so dependency arrays compare them cleanly
  const restaurantId = user?.restaurantId;

  const location = useLocation();
  const requestedCategory = location.state?.selectedCategory;//

  // ✅ 1. Load seating categories ONLY when restaurantId changes
  useEffect(() => {
    if (!restaurantId) return;

    const loadCategories = async () => {
      // This will now log strictly when the user logs in or switches locations
      console.log("Fetching categories for restaurantId:", restaurantId);
      try {
        const data = await getSeatingCategories(restaurantId);
        setCategories(data);
        const requestedMatch = data.find((cat) => cat === requestedCategory)
          || (requestedCategory && data.find((cat) => cat[0] === requestedCategory));
        setSelectedCategory(requestedMatch || data[0] || "");
      } catch (error) {
        console.error("Error loading categories:", error);
      }
    };

    loadCategories();
  }, [restaurantId, requestedCategory]);

  // ✅ 2. Load seating areas ONLY when category or restaurant changes
  useEffect(() => {
    if (!selectedCategory || !restaurantId) {
      setSeatingAreas([]);
      setSeatingAreasLoading(false);
      setSeatingAreasError(false);
      return;
    }

    let isCurrentRequest = true;
    setSeatingAreas([]);
    setSeatingAreasLoading(true);
    setSeatingAreasError(false);

    const loadSeatingAreas = async () => {
      try {
        const data = await getSeatingAreas(restaurantId, selectedCategory);
        if (isCurrentRequest) setSeatingAreas(data);
      } catch (error) {
        if (isCurrentRequest) {
          console.error("Error loading seating areas:", error);
          setSeatingAreasError(true);
        }
      } finally {
        if (isCurrentRequest) setSeatingAreasLoading(false);
      }
    };

    loadSeatingAreas();
    return () => {
      isCurrentRequest = false;
    };
  }, [selectedCategory, restaurantId]);

  return (
    <div className="min-h-[calc(100vh-178px)] px-4 py-4 text-slate-100">
      <div className="mx-auto max-w-7xl">
        <h1 className="mb-2 text-xl font-semibold">
          Welcome {user?.role}
        </h1>

        {/* Seating Category Dropdown */}
        <div className="mb-6 rounded-2xl border border-slate-800 p-4 shadow-sm">
          <label className="mb-2 block text-sm font-medium text-slate-300">Select Category:</label>
          <div className="overflow-x-auto pb-2">
            <div className="flex min-w-max gap-2 sm:gap-3">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`whitespace-nowrap rounded-xl px-4 py-2 text-sm font-medium transition ${
                    selectedCategory === cat
                      ? "bg-indigo-500 text-white shadow-lg shadow-indigo-500/20"
                      : "bg-slate-800 text-slate-200 hover:bg-slate-700"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </div>

        {selectedCategory && (
          <h2 className="mb-4 text-lg font-semibold">
            {selectedCategory}
          </h2>
        )}

        {/* Seating Areas Grid */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
          {!selectedCategory ? null : seatingAreasLoading ? (
            <div className="col-span-full flex items-center justify-center gap-3 py-10 text-sm text-slate-300" role="status" aria-live="polite">
              <span className="h-5 w-5 animate-spin rounded-full border-2 border-slate-500 border-t-indigo-400" aria-hidden="true" />
              Loading seating areas...
            </div>
          ) : seatingAreasError ? (
            <div className="col-span-full py-6 text-center text-sm text-rose-300" role="alert">
              Could not load seating areas. Please try selecting the category again.
            </div>
          ) : seatingAreas.length === 0 ? (
            <div className="col-span-full py-6 text-center text-sm text-slate-400" role="status">
              No seating areas found for this category.
            </div>
          ) : seatingAreas.map((seating) => (
            <div
              key={seating.id}
              onClick={() => setTouchSeat(seating.seating_name)}
              onDoubleClick={() => {
                if (seating.status !== 'billpending') {
                  navigate(`/order/${encodeURIComponent(seating.seating_name)}`, { state: { selectedCategory } });
                } else {
                  navigate(`/pendingbill/${encodeURIComponent(seating.seating_name)}`)
                }
              }}
              className={`cursor-pointer rounded-2xl border p-4 text-center font-medium shadow-sm transition hover:shadow-md ${
                seating.status === 'active'
                  ? 'border-emerald-500 bg-emerald-600 text-white'
                  : seating.status === 'billpending'
                  ? 'border-rose-500 bg-rose-600 text-white'
                  : 'border-gray-400 bg-transparent-9 text-slate-100'
              } ${seating.seating_name === touchSeat ? 'ring-2 ring-indigo-500' : ''}`}
            >
              <div className="flex flex-col items-center">
                <div>{seating.seating_name}</div>
                {(seating.status === "active" || seating.status === "billpending") && (
                  <div className="mt-2 text-xs text-white/80">
                    {seating.occupied_minutes != null ? `${seating.occupied_minutes} min` : "—"}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}